import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGuardStore } from "../context/GuardContext";
import axios from "axios";
import { jwtDecode } from "jwt-decode";

import { getToken } from "firebase/messaging";
import { messaging } from "../api/firebase";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function GuardDashboard() {
  const navigate = useNavigate();
  const { selectedGuard, setSelectedGuard, fetchGuardProfile } = useGuardStore();

  const [guardName, setGuardName] = useState("GUARD");
  const [totalAssignments, setTotalAssignments] = useState(0);
  const [totalIncidents, setTotalIncidents] = useState(0);
  const [totalNotification, setTotalNotification] = useState(0);
  const [shiftVip, setShiftVip] = useState(null);
  const [shiftLoading, setShiftLoading] = useState(true);
  const [activeIncidents, setActiveIncidents] = useState([]);
  const [incidentLoading, setIncidentLoading] = useState(true);

  const [showNotifyPopup, setShowNotifyPopup] = useState(false);
  const [notifyMessage, setNotifyMessage] = useState("");
  const [notifyLoading, setNotifyLoading] = useState(false);

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth <= 991;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (selectedGuard?.name) {
      setGuardName(selectedGuard.name);
      fetchTotalAssignments(selectedGuard.id);
      fetchTotalIncidents(selectedGuard.id);
      fetchTotalNotifications(selectedGuard.id);
      fetchShiftVip(selectedGuard.id);
      fetchActiveIncidents(selectedGuard.id);
    }
  }, [selectedGuard]);

  useEffect(() => {
    fetchGuardProfile();
  }, []);

  useEffect(() => {
    const syncGuardProfile = async () => {
      try {
        if (selectedGuard?.name) return;

        const stored = localStorage.getItem("selectedGuard");
        if (stored) {
          const parsed = JSON.parse(stored);
          setSelectedGuard(parsed);
          setGuardName(parsed.name);
          fetchTotalAssignments(parsed.id);
          fetchTotalIncidents(parsed.id);
          fetchTotalNotifications(parsed.id);
          fetchShiftVip(parsed.id);
          fetchActiveIncidents(parsed.id);
          return;
        }

        const token = localStorage.getItem("guardToken");
        if (!token) return;

        const decoded = jwtDecode(token);
        const username = decoded.username || decoded.sub || decoded.email;

        const res = await axios.get(`${BASE_URL}/api/officer/profile`, {
          params: { username },
          headers: { Authorization: `Bearer ${token}` },
        });

        const profile = Array.isArray(res.data) ? res.data[0] : res.data;

        setSelectedGuard(profile);
        setGuardName(profile.name);
        localStorage.setItem("selectedGuard", JSON.stringify(profile));

        fetchTotalAssignments(profile.id);
        fetchTotalIncidents(profile.id);
        fetchTotalNotifications(profile.id);
        fetchShiftVip(profile.id);
        fetchActiveIncidents(profile.id);
      } catch (err) {
        console.log("Guard Sync Error:", err);
      }
    };

    syncGuardProfile();
  }, []);

  const fetchTotalAssignments = async (guardId) => {
  try {
    const token = localStorage.getItem("guardToken");

    const res = await axios.get(
      `${BASE_URL}/api/assignments/guard/${guardId}/history`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    const history = Array.isArray(res.data) ? res.data : [];

    // ✅ Each record = one assignment
    setTotalAssignments(history.length);
  } catch (err) {
    console.error("Total Assignment Error:", err);
    setTotalAssignments(0);
  }
};

 const fetchTotalIncidents = async (guardId) => {
  try {
    const token = localStorage.getItem("guardToken");

    const res = await axios.get(
      `${BASE_URL}/api/duty/accidentreq/${guardId}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    const incidents = Array.isArray(res.data) ? res.data : [];

    // ✅ Each record = one incident
    setTotalIncidents(incidents.length);
  } catch (err) {
    console.error("Total Incidents Error:", err);
    setTotalIncidents(0);
  }
};

const fetchTotalNotifications = async (guardId) => {
  try {
    const token = localStorage.getItem("guardToken");

    const res = await axios.get(
      `${BASE_URL}/api/notification/${guardId}/guard`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    const notifications = Array.isArray(res.data) ? res.data : [];

    setTotalNotification(notifications.length);
  } catch (err) {
    console.error("Total Notifications Error:", err);
    setTotalNotification(0);
  }
};

  const fetchShiftVip = async (guardId) => {
    try {
      setShiftLoading(true);
      const token = localStorage.getItem("guardToken");

      const res = await axios.get(
        `${BASE_URL}/api/assignments/getvip/${guardId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const extracted =
        res.data?.data || res.data?.vip || res.data || null;

      setShiftVip(extracted);
    } catch {
      setShiftVip(null);
    } finally {
      setShiftLoading(false);
    }
  };

  const fetchActiveIncidents = async (guardId) => {
    try {
      setIncidentLoading(true);
      const token = localStorage.getItem("guardToken");

      const res = await axios.get(
        `${BASE_URL}/api/duty/accidentreq/${guardId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const all = res.data || [];

      const active = all.filter((item) => {
        const status =
          item.admin_status ||
          item.manager_status ||
          item.req ||
          "pending";

        const value = status.toLowerCase();
        return (
          value === "pending" ||
          value === "arise" ||
          value === "manager_accepted"
        );
      });

      setActiveIncidents(active.slice(0, 5));
    } catch {
      setActiveIncidents([]);
    } finally {
      setIncidentLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/login");
  };

  const stats = [
    {
      title: "Total Assignments",
      value: totalAssignments,
      icon: "fa-file-alt",
      color: "#1e40af",
    },
    {
      title: "Total Incidents",
      value: totalIncidents,
      icon: "fa-exclamation-triangle",
      color: "#f59e0b",
    },
    {
      title: "Notifications",
      value: totalNotification,
      icon: "fa-bell",
      color: "#10b981",
    },
  ];

  const handleNotificationPermission = async () => {
    try {
      setNotifyLoading(true);

      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setNotifyMessage("Notification permission not accepted.");
        setNotifyLoading(false);
        return;
      }

      const token = await getToken(messaging, {
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
      });

      if (token) {
        await axios.post(
          `${BASE_URL}/api/notification/subscribe`,
          {
            notificationToken: token,
            notificationSenderId: selectedGuard?.id,
            notificationSenderRole: "guard",
            notificationSenderName: guardName,
            notificationMessage: `${guardName} subscribed Successfully`,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("guardToken")}`,
            },
          }
        );
      }

      setTimeout(() => {
        setShowNotifyPopup(false);
        setNotifyLoading(false);
      }, 5000);
    } catch (err) {
      console.error(err);
      setNotifyLoading(false);
    }
  };

  useEffect(() => {
    if (Notification.permission === "granted") {
      setShowNotifyPopup(false);
    } else {
      setShowNotifyPopup(true);
      setNotifyMessage("Enable notifications to receive real-time alerts.");
    }
  }, []);

  return (
    <>
      <div className="guard-dashboard-wrapper">
        {/* Header */}
        <header className="page-header">
          <div className="header-content">
            <div className="header-title">
              <h1>Officer Dashboard</h1>
              <p>Monitor your duty status, assignments and alerts</p>
            </div>
            <button onClick={handleLogout} className="logout-btn">
              <i className="fas fa-sign-out-alt"></i>
              <span className="btn-text">Logout</span>
            </button>
          </div>
        </header>

        {/* Main Content */}
        <main className="page-main">
          {/* Welcome */}
          <section className="welcome-card">
            <h2>Welcome back, {guardName} 👮‍♂️</h2>
            <p className="welcome-subtitle">Stay vigilant. Your duty overview is below.</p>
          </section>

          {/* Stats */}
          <section className="stats-grid">
            {stats.map((stat, i) => (
              <div key={i} className="stat-card">
                <div className="stat-icon" style={{ backgroundColor: stat.color }}>
                  <i className={`fas ${stat.icon}`}></i>
                </div>
                <div className="stat-info">
                  <h3 className="stat-value">{stat.value}</h3>
                  <p className="stat-title">{stat.title}</p>
                </div>
              </div>
            ))}
          </section>

          {/* Current Shift */}
          <section className="shift-card">
            <h3>Current Duty Assignment</h3>
            {shiftLoading ? (
              <div className="loading-small">Loading duty...</div>
            ) : !shiftVip ? (
              <p className="no-data">No active duty assignment</p>
            ) : (
              <div className="shift-details">
                <div className="shift-info">
                  <strong>VIP:</strong> {shiftVip.name}
                </div>
                <div className="shift-info">
                  <strong>Designation:</strong> {shiftVip.designation || "N/A"}
                </div>
                <div className="shift-info">
                  <strong>Start:</strong> {shiftVip.startAt || "N/A"}
                </div>
                <div className="shift-info">
                  <strong>End:</strong> {shiftVip.endAt || "N/A"}
                </div>
                <div className="shift-info">
                  <strong>Status:</strong> 
                  <span className="status-badge active">{shiftVip.status || "Active"}</span>
                </div>
                <button 
                  onClick={() => navigate("/guardshift")} 
                  className="view-details-btn"
                >
                  View Full Details →
                </button>
              </div>
            )}
          </section>

          {/* Active Incidents */}
          <section className="incidents-card">
            <h3>Active Incidents</h3>
            {incidentLoading ? (
              <div className="loading-small">Loading incidents...</div>
            ) : activeIncidents.length === 0 ? (
              <p className="no-data">No active incidents reported</p>
            ) : (
              <div className="incidents-list">
                {activeIncidents.map((item, index) => {
                  const status =
                    item.admin_status ||
                    item.manager_status ||
                    item.req ||
                    "Pending";

                  return (
                    <div key={index} className="incident-item">
                      <div className="incident-header">
                        <span className="incident-number">#{index + 1}</span>
                        <span className={`status-badge ${status.toLowerCase()}`}>
                          {status}
                        </span>
                      </div>
                      <p className="incident-message">
                        {item.message || "No description provided"}
                      </p>
                    </div>
                  );
                })}
                <button 
                  onClick={() => navigate("/guardIncidentHistory")} 
                  className="view-all-btn"
                >
                  View All Incidents →
                </button>
              </div>
            )}
          </section>
        </main>

        {/* Notification Toast */}
        {showNotifyPopup && !isMobile && (
          <div className="notification-toast">
            <div className="toast-header">
              <i className="fas fa-bell"></i>
              <strong>Enable Notifications</strong>
            </div>
            <p>{notifyMessage}</p>
            <button
              onClick={handleNotificationPermission}
              disabled={notifyLoading}
              className="toast-btn"
            >
              {notifyLoading ? "Processing..." : "Allow Notifications"}
            </button>
          </div>
        )}
      </div>

      {/* Professional Blue-Gray Theme for Officers */}
      <style jsx>{`
        .guard-dashboard-wrapper {
          min-height: 90vh;
          background: linear-gradient(135deg, #e0e7ff 0%, #f0f4ff 100%);
          color: #1e293b;
          padding: 0;
          width: 100%;
          overflow-x: hidden;
        }

        .page-header {
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid rgba(59, 130, 246, 0.4);
          padding: 1.2rem 2rem;
          position: sticky;
          top: 0;
          z-index: 100;
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          max-width: 1400px;
          margin: 0 auto;
        }

        .header-title h1 {
          font-size: 1.8rem;
          font-weight: 700;
          color: #1e40af;
          margin: 0;
        }

        .header-title p {
          margin: 0.25rem 0 0;
          opacity: 0.8;
          font-size: 0.9rem;
          color: #64748b;
        }

        .logout-btn {
          background: #ef4444;
          color: white;
          border: none;
          padding: 0.6rem 1.2rem;
          border-radius: 10px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.3s ease;
        }

        .logout-btn:hover {
          background: #dc2626;
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(239, 68, 68, 0.3);
        }

        .page-main {
          padding: 1.5rem 2rem;
          max-width: 1400px;
          margin: 0 auto;
        }

        @media (max-width: 768px) {
          .page-main { padding: 1rem; }
          .page-header { padding: 1rem 1.5rem; }
        }

        .welcome-card {
          background: rgba(255, 255, 255, 0.8);
          backdrop-filter: blur(12px);
          border-radius: 16px;
          padding: 2rem;
          margin-bottom: 1.5rem;
          border: 1px solid rgba(59, 130, 246, 0.5);
          transition: all 0.3s ease;
          text-align: center;
        }

        .welcome-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 30px rgba(59, 130, 246, 0.25);
        }

        .welcome-card h2 {
          font-size: 2rem;
          font-weight: 800;
          color: #1e3a8a;
          margin-bottom: 0.5rem;
        }

        .welcome-subtitle {
          font-size: 1.1rem;
          color: #64748b;
          opacity: 0.9;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .stat-card {
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(12px);
          border-radius: 16px;
          padding: 1.8rem;
          border: 1px solid rgba(59, 130, 246, 0.4);
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 1.5rem;
        }

        .stat-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 15px 35px rgba(59, 130, 246, 0.3);
        }

        .stat-icon {
          width: 70px;
          height: 70px;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.8rem;
          flex-shrink: 0;
        }

        .stat-value {
          font-size: 2.4rem;
          font-weight: 800;
          margin: 0;
          color: #1e3a8a;
        }

        .stat-title {
          opacity: 0.9;
          margin-top: 0.4rem;
          font-size: 1rem;
          font-weight: 500;
          color: #64748b;
        }

        .shift-card,
        .incidents-card {
          background: rgba(255, 255, 255, 0.8);
          backdrop-filter: blur(12px);
          border-radius: 16px;
          padding: 2rem;
          margin-bottom: 1.5rem;
          border: 1px solid rgba(59, 130, 246, 0.5);
          transition: all 0.3s ease;
        }

        .shift-card:hover,
        .incidents-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 15px 35px rgba(59, 130, 246, 0.25);
        }

        .shift-card h3,
        .incidents-card h3 {
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 1.2rem;
          color: #1e40af;
          display: flex;
          align-items: center;
          gap: 0.8rem;
        }

        .shift-details {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 1rem;
          background: rgba(59, 130, 246, 0.05);
          padding: 1.5rem;
          border-radius: 12px;
        }

        .shift-info {
          font-size: 1.05rem;
          color: #334155;
        }

        .shift-info strong {
          color: #1e40af;
        }

        .status-badge {
          padding: 0.5rem 1rem;
          border-radius: 30px;
          font-weight: 600;
          font-size: 0.9rem;
          margin-left: 0.5rem;
        }

        .status-badge.active {
          background: #d4edda;
          color: #155724;
        }

        .view-details-btn,
        .view-all-btn {
          margin-top: 1.5rem;
          padding: 0.8rem 1.6rem;
          background: #1e40af;
          color: white;
          border: none;
          border-radius: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .view-details-btn:hover,
        .view-all-btn:hover {
          background: #1e3a8a;
          transform: translateY(-2px);
        }

        .incidents-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .incident-item {
          background: rgba(255, 255, 255, 0.9);
          padding: 1.4rem;
          border-radius: 14px;
          border-left: 5px solid #f59e0b;
          box-shadow: 0 6px 20px rgba(245, 158, 11, 0.15);
        }

        .incident-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.8rem;
        }

        .incident-number {
          font-weight: 700;
          color: #1e40af;
        }

        .status-badge.pending,
        .status-badge.arise,
        .status-badge.manager_accepted {
          background: #fff3cd;
          color: #856404;
        }

        .incident-message {
          color: #475569;
          line-height: 1.5;
          margin: 0;
        }

        .no-data {
          text-align: center;
          padding: 2rem;
          color: #64748b;
          font-style: italic;
        }

        .loading-small {
          text-align: center;
          padding: 1.5rem;
          color: #64748b;
        }

        .notification-toast {
          position: fixed;
          bottom: 30px;
          right: 30px;
          width: 340px;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(12px);
          border-radius: 14px;
          padding: 1.4rem;
          box-shadow: 0 15px 40px rgba(59, 130, 246, 0.3);
          border: 1px solid #3b82f6;
          z-index: 9999;
          animation: slideIn 0.5s ease;
          color: #1e293b;
        }

        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        .toast-header {
          display: flex;
          align-items: center;
          gap: 0.8rem;
          margin-bottom: 0.8rem;
          color: #1e40af;
          font-weight: 600;
        }

        .toast-btn {
          width: 100%;
          padding: 0.8rem;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 10px;
          font-weight: 600;
          margin-top: 1rem;
          transition: all 0.3s;
        }

        .toast-btn:hover {
          background: #2563eb;
        }

        .toast-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
      `}</style>
    </>
  );
}

// //----------------------------4-----------------------------------------

// import React, { useState, useEffect } from "react";
// import { useNavigate } from "react-router-dom";
// import { useGuardStore } from "../context/GuardContext";
// import axios from "axios";
// import { jwtDecode } from "jwt-decode";

// // ✅ ONLY ADDITION
// import { getToken } from "firebase/messaging";
// import { messaging } from "../api/firebase";

// const BASE_URL = process.env.REACT_APP_BASE_URL;

// export default function GuardDashboard() {
//   const navigate = useNavigate();
//   const { selectedGuard, setSelectedGuard, fetchGuardProfile } = useGuardStore();

//   const [guardName, setGuardName] = useState("GUARD");

//   const [totalAssignments, setTotalAssignments] = useState(0);
//   const [totalIncidents, setTotalIncidents] = useState(0);
//   const [totalNotification, setTotalNotification] = useState(0);

//   const [shiftVip, setShiftVip] = useState(null);
//   const [shiftLoading, setShiftLoading] = useState(true);

//   const [activeIncidents, setActiveIncidents] = useState([]);
//   const [incidentLoading, setIncidentLoading] = useState(true);

  
//     // 🔔 notification popup (ONLY ADDITION)
//     const [showNotifyPopup, setShowNotifyPopup] = useState(false);
//     const [notifyMessage, setNotifyMessage] = useState("");
//     const [notifyLoading, setNotifyLoading] = useState(false);
  

//   useEffect(() => {
//     if (selectedGuard?.name) {
//       setGuardName(selectedGuard.name);
//       fetchTotalAssignments(selectedGuard.id);
//       fetchTotalIncidents(selectedGuard.id);
//       fetchTotalNotifications(selectedGuard.id);
//       fetchShiftVip(selectedGuard.id);
//       fetchActiveIncidents(selectedGuard.id);
//     }
//   }, [selectedGuard]);

//   useEffect(() => {
//     fetchGuardProfile();
//   }, []);

//   useEffect(() => {
//     const syncGuardProfile = async () => {
//       try {
//         if (selectedGuard?.name) return;

//         const stored = localStorage.getItem("selectedGuard");
//         if (stored) {
//           const parsed = JSON.parse(stored);
//           setSelectedGuard(parsed);
//           setGuardName(parsed.name);
//           fetchTotalAssignments(parsed.id);
//           fetchTotalIncidents(parsed.id);
//           fetchTotalNotifications(parsed.id);
//           fetchShiftVip(parsed.id);
//           fetchActiveIncidents(parsed.id);
//           return;
//         }

//         const token = localStorage.getItem("guardToken");
//         if (!token) return;

//         const decoded = jwtDecode(token);
//         const username = decoded.username || decoded.sub || decoded.email;

//         const res = await axios.get(`${BASE_URL}/api/officer/profile`, {
//           params: { username },
//           headers: { Authorization: `Bearer ${token}` },
//         });

//         const profile = Array.isArray(res.data) ? res.data[0] : res.data;

//         setSelectedGuard(profile);
//         setGuardName(profile.name);
//         localStorage.setItem("selectedGuard", JSON.stringify(profile));

//         fetchTotalAssignments(profile.id);
//         fetchTotalIncidents(profile.id);
//         fetchTotalNotifications(profile.id);
//         fetchShiftVip(profile.id);
//         fetchActiveIncidents(profile.id);
//       } catch (err) {
//         console.log("Guard Sync Error:", err);
//       }
//     };

//     syncGuardProfile();
//   }, []);

//   const fetchTotalAssignments = async (guardId) => {
//     try {
//       const token = localStorage.getItem("guardToken");
//       const res = await axios.get(
//         `${BASE_URL}/api/assignments/guard/${guardId}/history`,
//         { headers: { Authorization: `Bearer ${token}` } }
//       );

//       const history = res.data || [];
//       const total = history.reduce(
//         (sum, item) => sum + (item.timesAssigned || 0),
//         0
//       );

//       setTotalAssignments(total);
//     } catch {
//       setTotalAssignments(0);
//     }
//   };

//   const fetchTotalIncidents = async (guardId) => {
//     try {
//       const token = localStorage.getItem("guardToken");
//       const res = await axios.get(
//         `${BASE_URL}/api/duty/accidentreq/${guardId}`,
//         { headers: { Authorization: `Bearer ${token}` } }
//       );

//       const incidents = res.data || [];
//       setTotalIncidents(incidents.length);
//     } catch {
//       setTotalIncidents(0);
//     }
//   };

//   const fetchTotalNotifications = async (guardId) => {
//     try {
//       const token = localStorage.getItem("guardToken");

//       const res = await axios.get(
//         `${BASE_URL}/api/notification/guard/${guardId}`,
//         { headers: { Authorization: `Bearer ${token}` } }
//       );

//       const list = Array.isArray(res.data) ? res.data : [];
//       setTotalNotification(list.length);
//     } catch {
//       setTotalNotification(0);
//     }
//   };

//   const fetchShiftVip = async (guardId) => {
//     try {
//       setShiftLoading(true);
//       const token = localStorage.getItem("guardToken");

//       const res = await axios.get(
//         `${BASE_URL}/api/assignments/getvip/${guardId}`,
//         { headers: { Authorization: `Bearer ${token}` } }
//       );

//       const extracted =
//         res.data?.data || res.data?.vip || res.data || null;

//       setShiftVip(extracted);
//     } catch {
//       setShiftVip(null);
//     } finally {
//       setShiftLoading(false);
//     }
//   };

//   const fetchActiveIncidents = async (guardId) => {
//     try {
//       setIncidentLoading(true);
//       const token = localStorage.getItem("guardToken");

//       const res = await axios.get(
//         `${BASE_URL}/api/duty/accidentreq/${guardId}`,
//         { headers: { Authorization: `Bearer ${token}` } }
//       );

//       const all = res.data || [];

//       const active = all.filter((item) => {
//         const status =
//           item.admin_status ||
//           item.manager_status ||
//           item.req ||
//           "pending";

//         const value = status.toLowerCase();
//         return (
//           value === "pending" ||
//           value === "arise" ||
//           value === "manager_accepted"
//         );
//       });

//       setActiveIncidents(active.slice(0, 5));
//     } catch {
//       setActiveIncidents([]);
//     } finally {
//       setIncidentLoading(false);
//     }
//   };

//   const handleLogout = () => {
//     localStorage.clear();
//     sessionStorage.clear();
//     navigate("/login");
//   };

//   const stats = [
//     {
//       title: "Total Assignments Assigned",
//       value: totalAssignments,
//       icon: "fas fa-file",
//       color: "#1e73be",
//     },
//     {
//       title: "Total Incident History",
//       value: totalIncidents,
//       icon: "fas fa-exclamation-triangle",
//       color: "#ffa500",
//     },
//     {
//       title: "Total Notifications",
//       value: totalNotification,
//       icon: "fas fa-bell",
//       color: "#28a745",
//     },
//   ];

  
//   /* -----------------------------------
//       🔔 NOTIFICATION (ONLY ADDITION)
//   ------------------------------------ */
//   const handleNotificationPermission = async () => {
//     try {
//       setNotifyLoading(true);

//       const permission = await Notification.requestPermission();
//       if (permission !== "granted") {
//         setNotifyMessage("Notification permission not accepted.");
//         setNotifyLoading(false);
//         return;
//       }

//       const token = await getToken(messaging, {
//         vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
//       });

//       if (token) {
//         await axios.post(
//           `${BASE_URL}/api/notification/subscribe`,
//           {
//             notificationToken: token,
//             notificationSenderId: selectedGuard?.id,
//             notificationSenderRole: "guard",
//             notificationSenderName: guardName,
//             notificationMessage:
//               `${guardName} subscribed Successfully`,
//           },
//           {
//             headers: {
//               Authorization: `Bearer ${localStorage.getItem("guardToken")}`,
//             },
//           }
//         );
//       }

//       setTimeout(() => {
//         setShowNotifyPopup(false);
//         setNotifyLoading(false);
//       }, 5000);
//     } catch (err) {
//       console.error(err);
//       setNotifyLoading(false);
//     }
//   };

//   useEffect(() => {
//     if (Notification.permission === "granted") {
//       setShowNotifyPopup(false);
//     } else {
//       setShowNotifyPopup(true);
//       setNotifyMessage("Please allow notification permission.");
//     }
//   }, []);

//   return (
//     <div style={styles.page}>
//       <div style={styles.headerSection}>
//         <div>
//           <h2 style={styles.pageTitle}>Officer Dashboard</h2>
//           <p style={styles.desc}>
//             Overview of assignments, incidents & system activity
//           </p>
//         </div>

//         <button style={styles.logoutBtn} onClick={handleLogout}>
//           <i className="fas fa-sign-out-alt" style={{ marginRight: 8 }}></i>
//           Logout
//         </button>
//       </div>

//       <div style={styles.mainContent}>
//         <div style={styles.welcomeBox}>
//           <h2 style={styles.welcomeText}>Welcome {guardName} 👋</h2>
//         </div>

//         <div style={styles.statsGrid}>
//           {stats.map((stat, index) => (
//             <div
//               key={index}
//               style={styles.statCard}
//               onMouseEnter={(e) => {
//                 e.currentTarget.style.transform = "scale(1.05)";
//                 e.currentTarget.style.boxShadow =
//                   "0 12px 25px rgba(0,0,0,0.15)";
//               }}
//               onMouseLeave={(e) => {
//                 e.currentTarget.style.transform = "scale(1)";
//                 e.currentTarget.style.boxShadow =
//                   "0 8px 20px rgba(0,0,0,0.1)";
//               }}
//             >
//               <div style={{ ...styles.iconCircle, background: stat.color }}>
//                 <i className={stat.icon} style={styles.icon}></i>
//               </div>
//               <h3 style={styles.statValue}>{stat.value}</h3>
//               <p style={styles.statTitle}>{stat.title}</p>
//             </div>
//           ))}
//         </div>

//         <div style={styles.shiftCard}>
//           <h3 style={styles.shiftTitle}>Current Officer Shift</h3>

//           <div style={{ overflowX: "auto" }}>
//             {shiftLoading ? (
//               <p>Loading shift...</p>
//             ) : !shiftVip ? (
//               <p>No Shift Assigned</p>
//             ) : (
//               <table style={styles.table}>
//                 <thead>
//                   <tr>
//                     <th style={styles.th}>Assignment ID</th>
//                     <th style={styles.th}>VIP Name</th>
//                     <th style={styles.th}>Designation</th>
//                     <th style={styles.th}>Start</th>
//                     <th style={styles.th}>End</th>
//                     <th style={styles.th}>Status</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   <tr
//                     style={styles.row}
//                     onClick={() => navigate("/guardshift")}
//                   >
//                     <td style={styles.td}>{shiftVip.id}</td>
//                     <td style={styles.td}>{shiftVip.name}</td>
//                     <td style={styles.td}>{shiftVip.designation}</td>
//                     <td style={styles.td}>{shiftVip.startAt || "N/A"}</td>
//                     <td style={styles.td}>{shiftVip.endAt || "N/A"}</td>
//                     <td style={styles.td}>{shiftVip.status || "Assigned"}</td>
//                   </tr>
//                 </tbody>
//               </table>
//             )}
//           </div>
//         </div>

//         <div style={styles.shiftCard}>
//           <h3 style={styles.shiftTitle}>Active Incidents</h3>

//           <div style={{ overflowX: "auto" }}>
//             {incidentLoading ? (
//               <p>Loading incidents...</p>
//             ) : activeIncidents.length === 0 ? (
//               <p>No Active Incidents</p>
//             ) : (
//               <table style={styles.table}>
//                 <thead>
//                   <tr>
//                     <th style={styles.th}>#</th>
//                     <th style={styles.th}>Status</th>
//                     <th style={styles.th}>Message</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {activeIncidents.map((item, index) => {
//                     const status =
//                       item.admin_status ||
//                       item.manager_status ||
//                       item.req ||
//                       "Pending";

//                     return (
//                       <tr
//                         key={index}
//                         style={styles.row}
//                         onClick={() =>
//                           navigate("/guardIncidentHistory")
//                         }
//                       >
//                         <td style={styles.td}>{index + 1}</td>
//                         <td style={styles.td}>{status}</td>
//                         <td style={styles.td}>{item.message || "-"}</td>
//                       </tr>
//                     );
//                   })}
//                 </tbody>
//               </table>
//             )}
          
//            {showNotifyPopup && (
//         <div style={{
//           position: "fixed",
//           bottom: 20,
//           right: 20,
//           width: 280,
//           background: "#fff",
//           padding: 14,
//           borderRadius: 8,
//           boxShadow: "0 4px 12px rgba(0,0,0,0.2)"
//         }}>
//           <h6>🔔 Notification Required</h6>
//           <p>{notifyMessage}</p>
//           <button
//             className="btn btn-primary w-100"
//             disabled={notifyLoading}
//             onClick={handleNotificationPermission}
//           >
//             {notifyLoading ? "Please wait..." : "Allow Notifications"}
//           </button>
//         </div>
//       )}
//       </div>
//         </div>
//       </div>
//     </div>
//   );
// }

// /* ========================== STYLES (ONLY TRANSITION ADDED) ========================== */
// const styles = {
//   page: {
//     padding: "20px",
//     background: "#f4f6f9",
//     minHeight: "81vh",
//   },
//   headerSection: {
//     display: "flex",
//     flexWrap: "wrap",
//     gap: "15px",
//     justifyContent: "space-between",
//     alignItems: "center",
//     marginBottom: 22,
//   },
//   pageTitle: {
//     fontSize: "clamp(22px, 4vw, 30px)",
//     fontWeight: 800,
//     color: "#1e73be",
//   },
//   desc: { fontSize: 14, color: "#666" },
//   logoutBtn: {
//     background: "linear-gradient(135deg,#1e73be,#4facfe)",
//     padding: "10px 20px",
//     borderRadius: 30,
//     color: "#fff",
//     border: "none",
//     cursor: "pointer",
//     fontWeight: 700,
//     boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
//   },
//   mainContent: {
//     background: "#fff",
//     padding: 22,
//     borderRadius: 18,
//     boxShadow: "0 10px 28px rgba(0,0,0,0.12)",
//   },
//   welcomeBox: { marginBottom: 20 },
//   welcomeText: {
//     fontSize: "clamp(18px, 4vw, 26px)",
//     fontWeight: 800,
//     color: "#222",
//   },
//   statsGrid: {
//     display: "grid",
//     gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
//     gap: 22,
//   },
//   statCard: {
//     background: "#ffffff",
//     borderRadius: 18,
//     padding: 20,
//     textAlign: "center",
//     boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
//     transition: "all 0.3s ease", //  hover animation enabled
//     cursor: "pointer",
//   },
//   iconCircle: {
//     width: 54,
//     height: 54,
//     borderRadius: "50%",
//     display: "flex",
//     alignItems: "center",
//     justifyContent: "center",
//     margin: "0 auto 10px",
//   },
//   icon: { fontSize: 18, color: "white" },
//   statValue: {
//     fontSize: 28,
//     fontWeight: 800,
//     color: "#1e73be",
//   },
//   statTitle: { fontSize: 13, color: "#777" },
//   shiftCard: {
//     marginTop: 30,
//     background: "#f9fbff",
//     padding: 18,
//     borderRadius: 18,
//     boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
//   },
//   shiftTitle: {
//     marginBottom: 10,
//     fontSize: 18,
//     fontWeight: 800,
//     color: "#1e73be",
//   },
//   table: {
//     width: "100%",
//     borderCollapse: "collapse",
//     background: "#fff",
//   },
//   th: {
//     padding: 12,
//     borderBottom: "2px solid #ddd",
//     textAlign: "left",
//     fontSize: 13,
//     color: "#333",
//   },
//   td: {
//     padding: 12,
//     borderBottom: "1px solid #eee",
//     fontSize: 13,
//     color: "#555",
//   },
//   row: { cursor: "pointer" },
// };

